import React from "react";
import { CardSkeleton } from "../../../components/skeleton/ListSkeleton";
import { getInventory } from "../analyticsApi";
import { formatNumber, formatPct, formatQty, formatRs, longDate, seriesIsEmpty } from "../analyticsUtils";
import { useAnalyticsPeriod } from "../useAnalyticsPeriod";
import { useAnalyticsFetch } from "../useAnalyticsFetch";
import PeriodPicker from "../components/PeriodPicker";
import KpiCard from "../components/KpiCard";
import EmptyState from "../components/EmptyState";
import { PeriodBarChart } from "../components/charts";
import { ChartCard, EmptyRow, TD, TD_NUM, TH, TH_NUM, TR, TableCard } from "../components/ui";

const HEAD = "border-b border-slate-200 dark:border-slate-600";

const KIND_LABEL: Record<string, string> = {
  patient: "patient",
  employee: "staff",
  prescription: "prescriptions",
  clinic: "clinic use",
  treatment: "treatment stages",
};

/** "patient Rs 800 · clinic use Rs 160 · 2 units uncosted" */
export function costByKindHint(cogs?: { byKind: Record<string, number | undefined>; uncostedQty: number } | null): string {
  if (!cogs) return "FIFO cost of everything that left stock in this period";
  const parts = Object.entries(cogs.byKind)
    .filter(([, v]) => Number(v) > 0)
    .map(([k, v]) => `${KIND_LABEL[k] || k} ${formatRs(v)}`);
  if (cogs.uncostedQty > 0) parts.push(`${formatNumber(cogs.uncostedQty)} units uncosted`);
  return parts.length ? parts.join(" · ") : "Nothing left stock in this period";
}

/** "Rs 300 sold · Rs 100 cost · 66.7%" */
export function marginHint(gm?: { revenue: number; cost: number; pct: number | null } | null): string {
  if (!gm) return "Patient and staff product sales against their FIFO cost";
  const pct = gm.pct == null ? "" : ` · ${formatPct(gm.pct)}`;
  return `${formatRs(gm.revenue)} sold · ${formatRs(gm.cost)} cost${pct}`;
}
const BODY = "divide-y divide-slate-100 dark:divide-slate-600";

export default function AnalyticsInventory() {
  const period = useAnalyticsPeriod();
  const { data, loading } = useAnalyticsFetch(() => getInventory({ from: period.from, to: period.to }), [period.from, period.to]);

  if (!loading && data && data.enabled === false) {
    return (
      <div className="space-y-4 text-slate-800 dark:text-slate-200">
        <EmptyState title="Inventory is not enabled for this clinic">Stock analytics appear once the Inventory module is switched on for this clinic.</EmptyState>
      </div>
    );
  }

  const inv = data && data.enabled ? data : null;
  const spendSeries = inv ? inv.spendSeries.map((p) => ({ bucket: p.month, total: p.total })) : [];

  return (
    <div className="space-y-4 text-slate-800 dark:text-slate-200">
      <PeriodPicker period={period} />

      {loading || !inv ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <CardSkeleton cards={6} height="h-28" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <KpiCard label="Stock value" value={formatRs(inv.stockValue)} hint="What the units on hand cost, oldest purchase first (FIFO)" />
            <KpiCard label="Cost of goods released" value={formatRs(inv.costOfGoods?.total)} hint={costByKindHint(inv.costOfGoods)} />
            <KpiCard label="Gross margin on sales" value={formatRs(inv.grossMargin?.margin)} hint={marginHint(inv.grossMargin)} />
            <KpiCard label="Products" value={formatNumber(inv.products)} hint={`${formatNumber(inv.inStock)} in stock`} />
            <KpiCard label="Below alert level" value={formatNumber(inv.belowAlert.length)} hint="Products at or under their alert quantity" />
            <KpiCard label="Dead stock" value={formatRs(inv.deadStock.value)} hint={`${formatNumber(inv.deadStock.count)} products with no release in 90 days`} />
            <KpiCard label="Supplier spend" value={formatRs(inv.supplierSpend.current)} change={inv.supplierSpend.change} neutral />
            <KpiCard label="Unpaid supplier invoices" value={formatRs(inv.unpaidInvoices.total)} hint={`${formatNumber(inv.unpaidInvoices.count)} invoices with a balance due`} />
          </div>

          <ChartCard title="Supplier spend by month" hint="Purchase invoices dated in this period">
            {seriesIsEmpty(spendSeries, ["total"]) ? (
              <p className="text-sm text-slate-400 dark:text-slate-500 py-10 text-center">No purchases in this period</p>
            ) : (
              <PeriodBarChart series={spendSeries} specs={[{ key: "total", label: "Spend" }]} format="rs" height={240} />
            )}
          </ChartCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TableCard title="Below alert level" hint="Reorder these first">
              <table className="w-full">
                <thead>
                  <tr className={HEAD}>
                    <th className={TH}>Product</th>
                    <th className={TH_NUM}>Available</th>
                    <th className={TH_NUM}>Alert at</th>
                    <th className={TH}>Unit</th>
                  </tr>
                </thead>
                <tbody className={BODY}>
                  {inv.belowAlert.length === 0 ? (
                    <EmptyRow colSpan={4}>Every product is above its alert level</EmptyRow>
                  ) : (
                    inv.belowAlert.map((row) => (
                      <tr key={row.productId} className={TR}>
                        <td className={TD}>{row.productName}</td>
                        <td className={`${TD_NUM} text-rose-600 dark:text-rose-400 font-medium`}>{formatNumber(row.availableQuantity)}</td>
                        <td className={TD_NUM}>{formatNumber(row.alertQuantity)}</td>
                        <td className={TD}>{row.scale || "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </TableCard>

            <TableCard title="Dead stock" hint="In stock but not released in the last 90 days · top 20 by value">
              <table className="w-full">
                <thead>
                  <tr className={HEAD}>
                    <th className={TH}>Product</th>
                    <th className={TH_NUM}>Quantity</th>
                    <th className={TH_NUM}>Value</th>
                  </tr>
                </thead>
                <tbody className={BODY}>
                  {inv.deadStock.list.length === 0 ? (
                    <EmptyRow colSpan={3}>Everything in stock has moved recently</EmptyRow>
                  ) : (
                    inv.deadStock.list.map((row) => (
                      <tr key={row.productId} className={TR}>
                        <td className={TD}>{row.productName}</td>
                        <td className={TD_NUM}>{formatQty(row.availableQuantity, row.scale)}</td>
                        <td className={TD_NUM}>{formatRs(row.value)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </TableCard>

            <TableCard title="Fast movers" hint="Most released in this period">
              <table className="w-full">
                <thead>
                  <tr className={HEAD}>
                    <th className={TH}>Product</th>
                    <th className={TH_NUM}>Released</th>
                    <th className={TH_NUM}>Left</th>
                  </tr>
                </thead>
                <tbody className={BODY}>
                  {inv.fastMovers.length === 0 ? (
                    <EmptyRow colSpan={3}>No releases in this period</EmptyRow>
                  ) : (
                    inv.fastMovers.map((row) => (
                      <tr key={row.productId} className={TR}>
                        <td className={TD}>{row.productName}</td>
                        <td className={TD_NUM}>{formatQty(row.released, row.scale)}</td>
                        <td className={TD_NUM}>{formatQty(row.availableQuantity, row.scale)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </TableCard>

            <TableCard title="By supplier" hint="Invoices dated in this period">
              <table className="w-full">
                <thead>
                  <tr className={HEAD}>
                    <th className={TH}>Supplier</th>
                    <th className={TH_NUM}>Invoices</th>
                    <th className={TH_NUM}>Total</th>
                    <th className={TH_NUM}>Due</th>
                  </tr>
                </thead>
                <tbody className={BODY}>
                  {inv.bySupplier.length === 0 ? (
                    <EmptyRow colSpan={4}>No purchases in this period</EmptyRow>
                  ) : (
                    inv.bySupplier.map((row) => (
                      <tr key={row.supplier} className={TR}>
                        <td className={TD}>{row.supplier}</td>
                        <td className={TD_NUM}>{formatNumber(row.invoices)}</td>
                        <td className={TD_NUM}>{formatRs(row.total)}</td>
                        <td className={`${TD_NUM} ${row.due > 0 ? "text-amber-600 dark:text-amber-400" : ""}`}>{formatRs(row.due)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </TableCard>
          </div>

          <TableCard title="Unpaid supplier invoices" hint={`${formatNumber(inv.unpaidInvoices.count)} invoices · ${formatRs(inv.unpaidInvoices.total)} due · any date, largest first`}>
            <table className="w-full">
              <thead>
                <tr className={HEAD}>
                  <th className={TH}>Invoice</th>
                  <th className={TH}>Supplier</th>
                  <th className={TH}>Date</th>
                  <th className={TH_NUM}>Total</th>
                  <th className={TH_NUM}>Due</th>
                </tr>
              </thead>
              <tbody className={BODY}>
                {inv.unpaidInvoices.list.length === 0 ? (
                  <EmptyRow colSpan={5}>Every supplier invoice is settled</EmptyRow>
                ) : (
                  inv.unpaidInvoices.list.map((row) => (
                    <tr key={row.purchaseId} className={TR}>
                      <td className={TD}>{row.invoiceNo || "—"}</td>
                      <td className={TD}>{row.supplier}</td>
                      <td className={TD}>{row.day ? longDate(row.day) : "—"}</td>
                      <td className={TD_NUM}>{formatRs(row.total)}</td>
                      <td className={`${TD_NUM} text-amber-600 dark:text-amber-400 font-medium`}>{formatRs(row.due)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </TableCard>
        </>
      )}
    </div>
  );
}
